import React, { useState, useEffect } from 'react';

const LockIcon = () => (
  <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

// Renders a read-only locked pill when locked=true, otherwise renders children (the editable input).
const LockedField = ({ locked, value, className = '', children }) => {
  if (!locked) return children;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 ${className}`}>
      <LockIcon />
      <span className="break-words">{value || '—'}</span>
    </div>
  );
};

const LiveStampingRequestModal = ({ service, walletBalance, onClose, onSubmit }) => {
  const serviceStamps = service.stamps || [{ id: 1, default_values: {}, locked_fields: {} }];
  
  // Initialize state array based on service stamps
  const [stampForms, setStampForms] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Common order fields
  const [deliveryMode, setDeliveryMode] = useState('digital');
  const [considerationAmount, setConsiderationAmount] = useState('');
  const [externalRefId, setExternalRefId] = useState('');
  const [orderNotes, setOrderNotes] = useState('');

  useEffect(() => {
    // Populate initial forms based on service config
    const initialForms = serviceStamps.map(stamp => {
      const defaults = stamp.defaultValues || stamp.default_values || {};
      return {
        stamp_index: stamp.id,
        article: defaults.article || '',
        denomination: defaults.denomination || '',
        quantity: defaults.quantity || 1,
        stamp_purchased_by: defaults.stamp_purchased_by || '',
        stamp_paid_by: defaults.stamp_paid_by || '',
        first_party: { 
          name: defaults.first_party_name || '', 
          address: defaults.first_party_address || '', 
          mobile: defaults.first_party_mobile || '' 
        },
        second_party: { 
          name: defaults.second_party_name || '', 
          address: defaults.second_party_address || '', 
          mobile: defaults.second_party_mobile || '' 
        },
        notes: defaults.notes || ''
      };
    });
    setStampForms(initialForms);
  }, [service]); // Run once on mount/service change

  if (!service || stampForms.length === 0) return null;

  // Calculate total: Service Cost + Sum of Stamp Denominations
  const stampDenominationsTotal = stampForms.reduce((sum, form) => {
    const denom = parseFloat(form.denomination) || 0;
    const qty = parseInt(form.quantity) || 1;
    return sum + (denom * qty);
  }, 0);
  const totalAmount = service.basePrice + stampDenominationsTotal;
  const canAfford = walletBalance >= totalAmount;

  const handleStampChange = (index, field, value) => {
    const newForms = [...stampForms];
    newForms[index] = { ...newForms[index], [field]: value };
    setStampForms(newForms);
  };

  const handlePartyChange = (index, partyType, field, value) => {
    const newForms = [...stampForms];
    newForms[index][partyType] = { 
      ...newForms[index][partyType], 
      [field]: value.toUpperCase() 
    };
    setStampForms(newForms);
  };

  const handleSubmit = async () => {
    if (!canAfford) return;
    setIsSubmitting(true);
    
    // Prepare payload matching new backend schema
    const orderData = {
      service_id: service.id, // Ensure snake_case for consistency via API call util
      serviceName: service.name, // Keep for frontend use if needed
      stamp_data: stampForms.map(form => ({
        stamp_index: form.stamp_index,
        article: form.article,
        denomination: form.denomination ? parseFloat(form.denomination) : undefined,
        quantity: form.quantity ? parseInt(form.quantity) : 1,
        stamp_purchased_by: form.stamp_purchased_by,
        stamp_paid_by: form.stamp_paid_by,
        first_party: form.first_party,
        second_party: form.second_party,
        notes: form.notes
      })),
      consideration_amount: considerationAmount ? parseFloat(considerationAmount) : undefined,
      delivery_mode: deliveryMode,
      external_ref_id: externalRefId,
      order_notes: orderNotes || undefined
    };

    const success = await onSubmit(orderData);
    setIsSubmitting(false);
    if (success) onClose();
  };

  // Validation: Check if current active tab is valid (basic check)
  // For improved UX, we might want to check ALL tabs to enable submit
  const currentForm = stampForms[activeTab];
  const activeConfig = serviceStamps[activeTab] || {};
  const activeLocked = activeConfig.locked_fields || activeConfig.lockedFields || {};
  const activeRequired = activeConfig.required_fields || activeConfig.requiredFields || {};

  // A field must be filled when: it is required AND not locked (locked = pre-filled by template).
  const isFieldSatisfied = (locked, required, value) =>
    locked || !required || !!String(value ?? '').trim();

  const isStampValid = (form, config) => {
    if (!form) return false;
    const locked = config.locked_fields || config.lockedFields || {};
    const required = config.required_fields || config.requiredFields || {};
    return (
      isFieldSatisfied(locked.article,            required.article,            form.article) &&
      isFieldSatisfied(locked.denomination,        required.denomination,        form.denomination) &&
      isFieldSatisfied(locked.quantity,            required.quantity,            form.quantity) &&
      isFieldSatisfied(locked.stamp_purchased_by,  required.stamp_purchased_by,  form.stamp_purchased_by) &&
      isFieldSatisfied(locked.stamp_paid_by,       required.stamp_paid_by,       form.stamp_paid_by) &&
      isFieldSatisfied(locked.notes,               required.notes,               form.notes) &&
      isFieldSatisfied(locked.first_party_name,    required.first_party_name,    form.first_party?.name) &&
      isFieldSatisfied(locked.first_party_mobile,  required.first_party_mobile,  form.first_party?.mobile) &&
      isFieldSatisfied(locked.first_party_address, required.first_party_address, form.first_party?.address) &&
      isFieldSatisfied(locked.second_party_name,   required.second_party_name,   form.second_party?.name) &&
      isFieldSatisfied(locked.second_party_mobile, required.second_party_mobile, form.second_party?.mobile) &&
      isFieldSatisfied(locked.second_party_address,required.second_party_address,form.second_party?.address)
    );
  };

  const isFormValid = stampForms.every((form, index) => isStampValid(form, serviceStamps[index] || {}));


  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-white flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
            aria-label="Back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h2 className="text-lg font-bold text-gray-800">{service.name}</h2>
            <p className="text-sm text-gray-500">
              {service.state} • {serviceStamps.length} Stamp{serviceStamps.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          
          {/* Sidebar Tabs (if multiple stamps) */}
          {serviceStamps.length > 1 && (
            <div className="bg-gray-50 w-full md:w-48 border-r overflow-y-auto flex md:flex-col">
              {serviceStamps.map((stamp, index) => (
                <button
                  key={stamp.id}
                  onClick={() => setActiveTab(index)}
                  className={`p-3 text-left text-sm font-medium border-b md:border-b-0 md:border-l-4 transition-all ${
                    activeTab === index 
                      ? 'bg-white text-indigo-700 border-indigo-600 shadow-sm' 
                      : 'text-gray-500 hover:bg-gray-100 border-transparent'
                  }`}
                >
                  <div className="font-bold">Stamp #{index + 1}</div>
                  <div className="text-xs truncate text-gray-400">
                    {(stampForms[index]?.article || 'Details')}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            {/* Global Settings (Only visible on first tab? Or a separate section? Users prefer inline. Let's put common fields below stamps or at top)
              * Actually, delivery mode etc are common. Let's put them in a "Common" section at bottom of every tab or just once?
              * Let's put them at the bottom of the "Summary" or just make them sticky?
              * For simplicity: Render them at the bottom of the form container.
            */}

            {/* Stamp Specific Inputs */}
            {currentForm && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-gray-800 text-lg border-b pb-2 flex-1">
                    Stamp #{activeTab + 1} Configuration
                    <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                      {activeLocked.article ? 'Fixed Article' : 'Select Article'}
                    </span>
                  </h3>
                </div>

                {/* Article & Denomination Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Article{activeRequired.article && !activeLocked.article && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    <LockedField locked={!!activeLocked.article} value={currentForm.article}>
                      <input className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" value={currentForm.article} onChange={e => handleStampChange(activeTab, 'article', e.target.value)} placeholder="e.g. Affidavit, Indemnity Bond" />
                    </LockedField>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Denomination (₹){activeRequired.denomination && !activeLocked.denomination && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    <LockedField locked={!!activeLocked.denomination} value={currentForm.denomination ? `₹${currentForm.denomination}` : ''}>
                      <input type="number" className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" value={currentForm.denomination} onChange={e => handleStampChange(activeTab, 'denomination', e.target.value)} placeholder="e.g. 100, 500" />
                    </LockedField>
                  </div>
                </div>

                {/* Quantity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity{activeRequired.quantity && !activeLocked.quantity && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    <LockedField locked={!!activeLocked.quantity} value={currentForm.quantity}>
                      <input type="number" min="1" className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500" value={currentForm.quantity} onChange={e => handleStampChange(activeTab, 'quantity', e.target.value)} placeholder="1" />
                    </LockedField>
                  </div>
                </div>

                {/* Parties */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {/* First Party */}
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                      First Party
                      {activeRequired.first_party_name && !activeLocked.first_party_name && <span className="text-red-500 ml-1">*</span>}
                      {activeLocked.first_party_name && <span className="ml-auto text-xs">🔒 Locked</span>}
                    </h4>
                    <div className="mb-2">
                      <LockedField locked={!!activeLocked.first_party_name} value={currentForm.first_party.name} className="uppercase">
                        <input className="w-full p-2 border rounded uppercase" placeholder="Name" value={currentForm.first_party.name} onChange={e => handlePartyChange(activeTab, 'first_party', 'name', e.target.value)} />
                      </LockedField>
                    </div>
                    <div className="mb-2">
                      <LockedField locked={!!activeLocked.first_party_address} value={currentForm.first_party.address} className="uppercase">
                        <textarea className="w-full p-2 border rounded uppercase text-sm" rows="2" placeholder="Address" value={currentForm.first_party.address} onChange={e => handlePartyChange(activeTab, 'first_party', 'address', e.target.value)} />
                      </LockedField>
                    </div>
                    <LockedField locked={!!activeLocked.first_party_mobile} value={currentForm.first_party.mobile}>
                      <input className="w-full p-2 border rounded" placeholder="Mobile" maxLength="10" value={currentForm.first_party.mobile} onChange={e => handlePartyChange(activeTab, 'first_party', 'mobile', e.target.value.replace(/\D/g, ''))} />
                    </LockedField>
                  </div>

                  {/* Second Party */}
                  <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                      Second Party
                      {activeRequired.second_party_name && !activeLocked.second_party_name && <span className="text-red-500 ml-1">*</span>}
                      {activeLocked.second_party_name && <span className="ml-auto text-xs">🔒 Locked</span>}
                    </h4>
                    <div className="mb-2">
                      <LockedField locked={!!activeLocked.second_party_name} value={currentForm.second_party.name} className="uppercase">
                        <input className="w-full p-2 border rounded uppercase" placeholder="Name" value={currentForm.second_party.name} onChange={e => handlePartyChange(activeTab, 'second_party', 'name', e.target.value)} />
                      </LockedField>
                    </div>
                    <div className="mb-2">
                      <LockedField locked={!!activeLocked.second_party_address} value={currentForm.second_party.address} className="uppercase">
                        <textarea className="w-full p-2 border rounded uppercase text-sm" rows="2" placeholder="Address" value={currentForm.second_party.address} onChange={e => handlePartyChange(activeTab, 'second_party', 'address', e.target.value)} />
                      </LockedField>
                    </div>
                    <LockedField locked={!!activeLocked.second_party_mobile} value={currentForm.second_party.mobile}>
                      <input className="w-full p-2 border rounded" placeholder="Mobile" maxLength="10" value={currentForm.second_party.mobile} onChange={e => handlePartyChange(activeTab, 'second_party', 'mobile', e.target.value.replace(/\D/g, ''))} />
                    </LockedField>
                  </div>
                </div>

                {/* Additional Info per Stamp */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Purchased By{activeRequired.stamp_purchased_by && !activeLocked.stamp_purchased_by && <span className="text-red-500 ml-0.5">*</span>}</label>
                    <LockedField locked={!!activeLocked.stamp_purchased_by} value={currentForm.stamp_purchased_by}>
                      <input className="w-full p-2 border rounded" value={currentForm.stamp_purchased_by} onChange={e => handleStampChange(activeTab, 'stamp_purchased_by', e.target.value)} placeholder="Name of Purchaser" />
                    </LockedField>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Paid By{activeRequired.stamp_paid_by && !activeLocked.stamp_paid_by && <span className="text-red-500 ml-0.5">*</span>}</label>
                    <LockedField locked={!!activeLocked.stamp_paid_by} value={currentForm.stamp_paid_by}>
                      <input className="w-full p-2 border rounded" value={currentForm.stamp_paid_by} onChange={e => handleStampChange(activeTab, 'stamp_paid_by', e.target.value)} placeholder="Name of Payer" />
                    </LockedField>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Property Description{activeRequired.notes && !activeLocked.notes && <span className="text-red-500 ml-0.5">*</span>}</label>
                  <LockedField locked={!!activeLocked.notes} value={currentForm.notes}>
                    <textarea className="w-full p-2 border rounded uppercase text-sm" rows="2" maxLength="200" placeholder="e.g. Survey number, land description..." value={currentForm.notes} onChange={e => handleStampChange(activeTab, 'notes', e.target.value.toUpperCase())} />
                  </LockedField>
                </div>
              </div>
            )}
            
            <hr className="my-4 border-gray-200" />
            
            {/* Common Details (Delivery etc) */}
            <div className="bg-gray-50 p-4 rounded-xl space-y-3">
              <h4 className="font-bold text-gray-700 text-sm uppercase">Order Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Delivery Mode</label>
                    <select
                      className="w-full p-2 border rounded text-sm"
                      value={deliveryMode}
                      onChange={e => setDeliveryMode(e.target.value)}
                    >
                      <option value="digital">Digital Only (Scan)</option>
                      <option value="physical">Physical Delivery</option>
                      <option value="both">Both</option>
                    </select>
                 </div>
                 <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Consideration Amount (Optional)</label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded text-sm"
                      placeholder="Total Transaction Value"
                      value={considerationAmount}
                      onChange={e => setConsiderationAmount(e.target.value)}
                    />
                 </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Order Notes (Optional)</label>
                <textarea
                  className="w-full p-2 border rounded text-sm"
                  rows="2"
                  maxLength="1000"
                  placeholder="Any additional remarks for this order (e.g. urgent, special instructions)..."
                  value={orderNotes}
                  onChange={e => setOrderNotes(e.target.value)}
                />
              </div>
            </div>

          </div>
        </div>

        {/* Footer with Summary */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="hidden md:block">
            <div className="text-sm font-bold text-gray-700">Wallet Balance: <span className={canAfford ? 'text-green-600' : 'text-red-600'}>₹{walletBalance}</span></div>
            {!canAfford && <div className="text-xs text-red-500">Insufficient funds</div>}
          </div>
          
          <div className="flex space-x-3 w-full md:w-auto justify-end">
             {activeTab > 0 && (
               <button onClick={() => setActiveTab(activeTab - 1)} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg">
                 ← Prev Stamp
               </button>
             )}
             
             {activeTab < serviceStamps.length - 1 ? (
               <button onClick={() => setActiveTab(activeTab + 1)} className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                 Next Stamp →
               </button>
             ) : (
                <button 
                  onClick={handleSubmit} 
                  disabled={!isFormValid || !canAfford || isSubmitting} 
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-bold disabled:opacity-50 flex items-center"
                >
                  {isSubmitting ? (
                    'Processing...'
                  ) : (
                    <>
                       Pay ₹{stampDenominationsTotal}
                       <span className="ml-2 bg-green-700 text-xs px-2 py-0.5 rounded">Stamp Duty</span>
                    </>
                  )}
                </button>
             )}
          </div>
        </div>
        
        {/* Mobile Wallet Warning */}
        {!canAfford && (
          <div className="md:hidden px-6 pb-2 text-xs text-red-600 text-center font-bold">
            Insufficient Wallet Balance (₹{walletBalance})
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveStampingRequestModal;
